import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import ModalService from "shared/modals/ModalService";
import { type Nullable } from "shared/types/utility";

export interface AIFeedbackProps {
  aiFeatureConfigurationId?: number;
  aiInput: Record<string, unknown>;
  aiResult: Record<string, unknown>;
  animate?: boolean;
  showComponent?: boolean;
  showRegenerate?: boolean;
  source: string;
}

export interface AIFeedbackRecordedEvent {
  feedbackId: number;
  thumbsUp: boolean;
}

export interface AIFeedbackEmit {
  (event: "regenerate"): void;
  (event: "feedbackRecorded", feedback: AIFeedbackRecordedEvent): void;
}

interface AIFeedbackResponse {
  message: string;
  feedback_id: number;
}

interface AIFeedbackControls {
  disabled: boolean;
  icon: string;
  key: string;
  show: boolean;
  tooltip: string;
  onClick: () => void;
}

export default function useAIFeedback(
  props: AIFeedbackProps,
  { emit }: { emit: AIFeedbackEmit }
) {
  const feedbackId = ref<Nullable<number>>(null);
  const recordingFeedback = ref(false);
  const thumbsDownSubmitted = ref(false);
  const thumbsUpSubmitted = ref(false);

  const controlButtons = computed<AIFeedbackControls[]>(() => {
    const buttons: AIFeedbackControls[] = [
      {
        disabled: recordingFeedback.value,
        icon: "reload",
        key: "regenerate",
        show: Boolean(props.showRegenerate),
        tooltip: getLocaleText("ai_feedback.tool_tip_regenerate"),
        onClick: handleOnRegenerate,
      },
      {
        disabled: recordingFeedback.value,
        icon: thumbsUpSubmitted.value ? "thumbs-up-filled" : "thumbs-up",
        key: "thumbs-up",
        show: true,
        tooltip: thumbsUpTooltip.value,
        onClick: feedbackThumbsUp,
      },
      {
        disabled: recordingFeedback.value,
        icon: thumbsDownSubmitted.value ? "thumbs-down-filled" : "thumbs-down",
        key: "thumbs-down",
        show: true,
        tooltip: thumbsDownTooltip.value,
        onClick: feedbackThumbsDown,
      },
    ];

    return buttons.filter((button) => button.show);
  });

  const thumbsUpTooltip = computed(() => {
    if (thumbsDownSubmitted.value)
      return getLocaleText("ai_feedback.feedback_submitted");

    if (thumbsUpSubmitted.value)
      return getLocaleText("ai_feedback.remove_submitted_feedback");

    return getLocaleText("ai_feedback.tool_tip_helpful");
  });

  const thumbsDownTooltip = computed(() => {
    if (thumbsUpSubmitted.value)
      return getLocaleText("ai_feedback.feedback_submitted");

    if (thumbsDownSubmitted.value)
      return getLocaleText("ai_feedback.remove_submitted_feedback");

    return getLocaleText("ai_feedback.tool_tip_not_helpful");
  });

  function feedbackThumbsUp() {
    if (thumbsDownSubmitted.value) {
      return;
    }

    if (thumbsUpSubmitted.value) {
      removeSubmittedFeedback();

      return;
    }

    if (props.aiInput === undefined || props.aiResult === undefined) {
      return;
    }

    recordSubmittedFeedback({
      thumbsUp: true,
      selectedReason: null,
      input: props.aiInput,
      result: props.aiResult,
    });
  }

  function feedbackThumbsDown() {
    if (thumbsUpSubmitted.value) {
      return;
    }

    if (thumbsDownSubmitted.value) {
      removeSubmittedFeedback();

      return;
    }

    ModalService.open("AIFeedbackReasonModal", {
      events: {
        submitFeedback: recordSubmittedFeedback,
      },
    });
  }

  async function recordSubmittedFeedback({
    thumbsUp,
    selectedReason,
  }: {
    thumbsUp: boolean;
    selectedReason: Nullable<string>;
    input: Record<string, unknown>;
    result: Record<string, unknown>;
  }) {
    if (recordingFeedback.value) return;

    try {
      recordingFeedback.value = true;

      const { data } = await $streemApiV1.post<AIFeedbackResponse>(
        "ai/feedbacks",
        {
          params: {
            feedback: {
              ai_feature_configuration_id: props.aiFeatureConfigurationId,
              generated_result: props.aiResult,
              input: props.aiInput,
              source: props.source,
              thumbs_down_reason: selectedReason,
              thumbs_up: thumbsUp,
            },
          },
        }
      );

      feedbackId.value = data.feedback_id;

      if (thumbsUp) {
        thumbsUpSubmitted.value = true;
        track(`AI feedback thumbs up submitted for ${props.source}`);
      } else {
        thumbsDownSubmitted.value = true;

        track(
          `AI feedback thumbs down with reason ${selectedReason} submitted for ${props.source}`
        );
      }

      emit("feedbackRecorded", { feedbackId: feedbackId.value, thumbsUp });
    } catch (error) {
      ErrorDialog(
        getLocaleText("ai_feedback.error_submitting_feedback"),
        error
      );
    } finally {
      recordingFeedback.value = false;
    }
  }

  async function removeSubmittedFeedback() {
    try {
      await $streemApiV1.delete(`ai/feedbacks/${feedbackId.value}`);

      resetFeedbackThumbs();
    } catch (error) {
      ErrorDialog(
        getLocaleText("ai_feedback.error_submitting_feedback"),
        error
      );
    }
  }

  function resetFeedbackThumbs() {
    thumbsDownSubmitted.value = false;
    thumbsUpSubmitted.value = false;
    feedbackId.value = 0;
  }

  function handleOnRegenerate() {
    emit("regenerate");
    resetFeedbackThumbs();
  }

  function setFeedback(id: number, thumbUp: boolean) {
    feedbackId.value = id;
    thumbsDownSubmitted.value = !thumbUp;
    thumbsUpSubmitted.value = thumbUp;
  }

  return {
    controlButtons,
    feedbackId,
    recordingFeedback,
    thumbsDownSubmitted,
    thumbsUpSubmitted,

    feedbackThumbsDown,
    feedbackThumbsUp,
    handleOnRegenerate,
    recordSubmittedFeedback,
    removeSubmittedFeedback,
    setFeedback,
  };
}
